import * as Popover from '@radix-ui/react-popover';
import { cloneElement, useState, type MouseEvent, type ReactElement, type ReactNode } from 'react';
import { OptionList, type OptionItem } from './OptionList';

interface BaseProps {
  /** The element that opens the picker (must forward refs / props, e.g. a <button>). */
  trigger: ReactElement<{ onClick?: (e: MouseEvent) => void }>;
  items: OptionItem[];
  label: string;
  searchable?: boolean;
  align?: 'start' | 'end';
  /**
   * Grid cells render thousands of pickers. A lazy picker is a plain button until it is first
   * clicked, and only then mounts the Radix popover (already open).
   */
  lazy?: boolean;
}

/** Shared popover shell + lazy mounting. */
function PickerShell({
  trigger,
  lazy,
  align,
  label,
  open,
  onOpenChange,
  children,
}: {
  trigger: BaseProps['trigger'];
  lazy?: boolean;
  align: 'start' | 'end';
  label: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}) {
  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="pop" align={align} sideOffset={4} collisionPadding={8} aria-label={label} data-lazy={lazy || undefined}>
          {children}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

interface SingleProps extends BaseProps {
  value: string | null;
  onChange: (value: string | null) => void;
  /** Label of the "no value" entry; omit to disallow clearing. */
  clearLabel?: string;
}

/** Popover single-select; closes and reports the choice immediately. */
export function SingleSelect({ trigger, items, label, searchable, value, onChange, clearLabel, align = 'start', lazy }: SingleProps) {
  const [open, setOpen] = useState(false);
  const [armed, setArmed] = useState(!lazy);
  if (!armed) return cloneElement(trigger, { onClick: () => { setArmed(true); setOpen(true); } });
  const change = (next: boolean) => {
    setOpen(next);
    if (!next && lazy) setArmed(false);
  };
  const pick = (v: string | null) => {
    change(false);
    if (v !== value) onChange(v);
  };
  return (
    <PickerShell trigger={trigger} lazy={lazy} align={align} label={label} open={open} onOpenChange={change}>
      <OptionList
        items={items}
        selected={value ? [value] : []}
        multiple={false}
        label={label}
        searchable={searchable ?? items.length > 7}
        onToggle={(v) => pick(v)}
        clearOption={clearLabel ? { label: clearLabel, onSelect: () => pick(null) } : undefined}
      />
    </PickerShell>
  );
}

interface MultiProps extends BaseProps {
  values: string[];
  /** Called once when the popover closes with a changed selection. */
  onCommit: (values: string[]) => void;
}

const sameSet = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x) => b.includes(x));

/** Popover multi-select with a draft selection committed on close (one save per edit, not per click). */
export function MultiSelect({ trigger, items, label, searchable, values, onCommit, align = 'start', lazy }: MultiProps) {
  const [open, setOpen] = useState(false);
  const [armed, setArmed] = useState(!lazy);
  const [draft, setDraft] = useState<string[]>(values);
  if (!armed) {
    return cloneElement(trigger, {
      onClick: () => {
        setDraft(values);
        setArmed(true);
        setOpen(true);
      },
    });
  }
  const change = (next: boolean) => {
    if (next) setDraft(values);
    else {
      if (!sameSet(draft, values)) onCommit(draft);
      if (lazy) setArmed(false);
    }
    setOpen(next);
  };
  return (
    <PickerShell trigger={trigger} lazy={lazy} align={align} label={label} open={open} onOpenChange={change}>
      <OptionList
        items={items}
        selected={draft}
        multiple
        label={label}
        searchable={searchable ?? items.length > 7}
        onToggle={(v) => setDraft((d) => (d.includes(v) ? d.filter((x) => x !== v) : [...d, v]))}
        footer={<div className="border-t border-line px-2 py-1 text-[12.5px] text-faint">Saved when you close this list</div>}
      />
    </PickerShell>
  );
}
